import { useState } from "react";
import { useAddonShortfall } from "@/hooks/useAddonShortfall";
import { Button } from "@/components/ui/button";
import { AddonRemovalPicker } from "@/components/AddonRemovalPicker";
import { openBillingPortal } from "@/lib/billing";
import { AlertTriangle, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

/**
 * Persistent banner that appears when the user's add-on cancellation has
 * left them owning more workspaces than their plan allows. Stays until
 * they've nominated enough workspaces for archive.
 */
export const AddonShortfallBanner = () => {
  const { shortfall, archiveAt } = useAddonShortfall();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (shortfall <= 0 || !archiveAt) return null;

  const dateLabel = new Date(archiveAt).toLocaleDateString();

  return (
    <>
      <div className="bg-orange-500/15 border border-orange-500/40 rounded-md px-4 py-3 text-sm space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
          <span className="flex-1 min-w-[200px]">
            Your add-on cancellation drops your workspace allowance. Pick{" "}
            <strong>{shortfall}</strong> workspace{shortfall === 1 ? "" : "s"} to archive on{" "}
            <strong>{dateLabel}</strong>.
          </span>
          <Button size="sm" onClick={() => setPickerOpen(true)}>Choose workspaces</Button>
        </div>

        <button
          type="button"
          onClick={() => setDetailsOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          aria-expanded={detailsOpen}
        >
          {detailsOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          What this means
        </button>

        {detailsOpen && (
          <ul className="ml-5 list-disc space-y-1 text-xs text-muted-foreground">
            <li>You keep full access to every workspace until <strong>{dateLabel}</strong>.</li>
            <li>Your add-on subscription stops billing after <strong>{dateLabel}</strong> — no further charges, no proration owed.</li>
            <li>The workspaces you nominate become read-only on that date and are permanently deleted 30 days later. Export their data first if you want to keep it.</li>
            <li>Need to undo? Re-purchase an add-on from any workspace switcher before <strong>{dateLabel}</strong> and the archive is cancelled.</li>
          </ul>
        )}

        {detailsOpen && (
          <button
            type="button"
            onClick={() => void openBillingPortal()}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Manage subscription in billing portal <ExternalLink className="h-3 w-3" />
          </button>
        )}
      </div>
      <AddonRemovalPicker open={pickerOpen} onOpenChange={setPickerOpen} shortfall={shortfall} archiveAt={archiveAt} />
    </>
  );
};
